import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { FaTimes, FaFileAlt, FaSpinner, FaDownload, FaEye } from "react-icons/fa";

const FilePreviewModal = ({ file, onClose }) => {
  const [fileUrl, setFileUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const modalRoot = typeof document !== 'undefined' ? document.body : null;

  useEffect(() => {
    if (file) {
      setLoading(true);
      let url = "";
      if (file.file) {
        // For newly uploaded files
        url = URL.createObjectURL(file.file);
      } else if (file.url) {
        // For already stored files
        url = file.url.startsWith('http') ? file.url : `${process.env.REACT_APP_API_BASE_URL}${file.url}`;
      }
      
      setFileUrl(url);
      
      // If it's an image, we can hide loading after it loads
      // If it's a PDF in iframe, it's harder to detect
      if (!file.type || !file.type.startsWith('image/')) {
        setLoading(false);
      }

      return () => {
        if (file.file) URL.revokeObjectURL(url);
      };
    }
  }, [file]);

  useEffect(() => {
    if (!modalRoot) return;
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = "";
    };
  }, [modalRoot]);

  if (!modalRoot || !file) {
    return null;
  }

  const isImage = file.type && file.type.startsWith('image/');
  const isPDF = file.type === 'application/pdf' || (file.name && file.name.toLowerCase().endsWith('.pdf'));

  return createPortal(
    <div className="fixed inset-0 z-[1300] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-2xl w-full max-w-5xl h-full max-h-[90vh] flex flex-col overflow-hidden animate-scale-in">
        {/* Header */}
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-gray-100 dark:border-dark-700 bg-white dark:bg-dark-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary-50 dark:bg-primary-900/20 flex items-center justify-center text-primary-500">
              <FaEye className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg md:text-xl font-bold text-gray-800 dark:text-white truncate max-w-[200px] md:max-w-md">
                {file.name}
              </h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                File Preview
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a 
              href={fileUrl} 
              download={file.name}
              className="p-2.5 rounded-xl bg-gray-100 dark:bg-dark-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-dark-600 transition-colors"
              title="Download File"
            >
              <FaDownload className="w-4 h-4" />
            </a>
            <button
              onClick={onClose}
              className="p-2.5 rounded-xl bg-red-50 text-red-500 hover:bg-red-100 transition-colors"
              title="Close Preview"
            >
              <FaTimes className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto bg-gray-100 dark:bg-dark-900 flex items-center justify-center p-4">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/50 dark:bg-dark-800/50 z-10">
              <div className="flex flex-col items-center">
                <FaSpinner className="w-8 h-8 animate-spin text-primary-500 mb-2" />
                <p className="text-sm text-gray-500">Loading preview...</p>
              </div>
            </div>
          )}

          {isImage ? (
            <img 
              src={fileUrl} 
              alt={file.name} 
              className="max-w-full max-h-full object-contain rounded-lg shadow-lg"
              onLoad={() => setLoading(false)}
            />
          ) : isPDF ? (
            <iframe
              src={`${fileUrl}#toolbar=0`}
              className="w-full h-full border-0 rounded-lg bg-white"
              title="PDF Preview"
              onLoad={() => setLoading(false)}
            />
          ) : (
            <div className="text-center p-8 bg-white dark:bg-dark-800 rounded-2xl shadow-xl max-w-sm">
              <FaFileAlt className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-2">Preview Not Available</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                We can't preview this file type directly. Please download it to view.
              </p>
              <a 
                href={fileUrl} 
                download={file.name}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-primary-500 text-white font-semibold hover:bg-primary-600 transition-colors shadow-lg shadow-primary-500/30"
              >
                <FaDownload />
                Download File
              </a>
            </div>
          )}
        </div>
      </div>
    </div>,
    modalRoot
  );
};

export default FilePreviewModal;
